import { ActSummary, formatDuration, percent } from '@/lib/actModes'
import { cn } from '@/lib/utils'

function Tile({ value, label, hint, tone }: {
  value: string
  label: string
  hint?: string
  tone?: 'good' | 'warn' | 'plain'
}) {
  return (
    <div className="p-4 border border-border rounded-xl bg-background">
      <p className={cn(
        'text-2xl font-bold tabular-nums',
        tone === 'good' ? 'text-green-600' : tone === 'warn' ? 'text-amber-600' : 'text-primary'
      )}>
        {value}
      </p>
      <p className="text-xs font-medium mt-1">{label}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  )
}

/** Describes which way his time guesses lean, in words rather than a signed number. */
export function biasLabel(bias: number): string {
  if (bias === 0) return 'dead on average'
  return bias > 0
    ? `runs ${formatDuration(bias)} longer than you guess`
    : `finishes ${formatDuration(Math.abs(bias))} sooner than you guess`
}

/**
 * The four numbers worth looking at, in the order he'd ask them: how many, how
 * many right, how long each took, and how well he read himself going in.
 */
export default function ActSummaryTiles({ summary, compact }: { summary: ActSummary; compact?: boolean }) {
  const accuracyTone = summary.accuracy >= 0.7 ? 'good' : summary.accuracy >= 0.5 ? 'plain' : 'warn'
  const focusTone = summary.focusAccuracy >= 0.65 ? 'good' : summary.focusAccuracy >= 0.4 ? 'plain' : 'warn'

  return (
    <div className={cn('grid gap-3', compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4')}>
      <Tile
        value={String(summary.questionsCompleted)}
        label="Questions"
        hint={summary.totalActualSeconds > 0 ? `${formatDuration(summary.totalActualSeconds)} of work` : undefined}
      />
      <Tile
        value={percent(summary.accuracy)}
        label="Correct"
        hint={`${summary.correctQuestions} of ${summary.gradedQuestions}`}
        tone={accuracyTone}
      />
      <Tile
        value={formatDuration(summary.avgActualSeconds)}
        label="Per question"
        hint={summary.pacedQuestions > 0
          ? `${summary.onPaceQuestions} of ${summary.pacedQuestions} inside budget`
          : summary.timedQuestions > 0 ? biasLabel(summary.avgTimeBiasSeconds) : undefined}
      />
      <Tile
        value={percent(summary.focusAccuracy)}
        label="Called it right"
        hint={`${summary.focusHits} of ${summary.questionsCompleted} questions`}
        tone={focusTone}
      />
    </div>
  )
}

/** The four calibration buckets: where confidence and reality agreed or didn't. */
export function CalibrationGrid({ summary }: { summary: ActSummary }) {
  const cells = [
    { label: 'Said yes, got it', value: summary.confidentAndRight, tone: 'text-green-600', hint: 'Confidence earned' },
    { label: 'Said yes, missed', value: summary.confidentButWrong, tone: 'text-red-600', hint: 'Overconfident' },
    { label: 'Said no, got it', value: summary.doubtedButRight, tone: 'text-amber-600', hint: 'Selling yourself short' },
    { label: 'Said no, missed', value: summary.doubtedAndWrong, tone: 'text-muted-foreground', hint: 'You knew' },
  ]
  return (
    <div className="grid grid-cols-2 gap-3">
      {cells.map(cell => (
        <div key={cell.label} className="p-3 border border-border rounded-xl">
          <p className={cn('text-xl font-bold tabular-nums', cell.tone)}>{cell.value}</p>
          <p className="text-xs font-medium">{cell.label}</p>
          <p className="text-[11px] text-muted-foreground">{cell.hint}</p>
        </div>
      ))}
    </div>
  )
}
